import { useEffect, useState, useCallback, useRef } from "react";
import { isEqual } from "lodash";
import store from "store";
import { commonActions } from "store/common";
import { IUpsertUserRequest, User } from "../types/user";
import { createUserApi, fetchSuggestCodeApi, fetchUsersApi, updateUserApi } from "../api/userApi";

/**
 * the hook for user list
 */
const useUserList = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [query, setQuery] = useState({
    email_address: "",
    user_name: "",
    user_type: "",
    limit: 50,
    offset: 1,
    status_flag: 1,
    affiliation: "",
  });
  const [tempQuery, setTempQuery] = useState({
    email_address: "",
    user_name: "",
    user_type: "",
    status_flag: 1,
    affiliation: "",
  });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [hasMore, setHasMore] = useState(0);
  const [err, setErr] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [suggestCodeList, setSuggestCodeList] = useState([]);
  const isLoadingMoreRef = useRef(false);

  const fetchData = async (refetch = false) => {
    setIsFetching(true);
    const payload = {
      ...(query.email_address.length > 0 ? { email_address: query.email_address } : {}),
      ...(query.user_name.length > 0 ? { user_name: query.user_name } : {}),
      ...(query.user_type.length > 0 ? { user_type: query.user_type } : {}),
      ...(query.affiliation.length > 0 ? { affiliation: query.affiliation } : {}),
      limit: query.limit,
      offset: refetch ? 1 : query.offset,
      ...(query.status_flag === 1 ? { status_flag: 1 } : {}),
    };
    try {
      const { list, total } = await fetchUsersApi(payload);
      if (query.offset > 1 && !refetch) {
        // Load more: append to existing list
        setUsers((prevUsers) => [...prevUsers, ...list]);
      } else {
        // First load or refetch: replace the entire list
        setUsers(list);
      }
      setTotalItems(total);
      // Calculate how many items we've loaded so far
      const loadedItems = query.offset * query.limit;
      // If we've loaded all items or the returned list is less than limit, no more items
      setHasMore(loadedItems < total && list.length >= query.limit ? 1 : 0);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsFetching(false);
      isLoadingMoreRef.current = false;
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    query.offset,
    query.email_address,
    query.user_name,
    query.user_type,
    query.status_flag,
    query.affiliation,
  ]);

  useEffect(() => {
    if (!tempQuery.email_address.trim().length) {
      setErr(false);
    }
  }, [tempQuery.email_address]);

  const onNextPage = useCallback(() => {
    if (isFetching || hasMore === 0 || isLoadingMoreRef.current) {
      return;
    }
    isLoadingMoreRef.current = true;
    setQuery((prevQuery) => ({ ...prevQuery, offset: prevQuery.offset + 1 }));
  }, [isFetching, hasMore, query.offset]);

  const onSearch = async () => {
    if (
      isEqual(tempQuery, {
        email_address: query.email_address,
        user_name: query.user_name,
        user_type: query.user_type,
        status_flag: query.status_flag,
        affiliation: query.affiliation,
      })
    ) {
      return;
    }
    // Reset users array when searching to avoid showing old data
    setUsers([]);
    isLoadingMoreRef.current = false;
    setQuery((prevQuery) => ({ ...prevQuery, offset: 1, ...tempQuery }));
  };

  const onChangeCategoryType = (categoryType: string) => {
    if (isFetching) return;
    setTempQuery((prevQuery) => ({
      ...prevQuery,
      user_type: prevQuery.user_type === categoryType ? "" : categoryType,
    }));
  };

  const handleUpdateUser = async (updateRequest: IUpsertUserRequest) => {
    if (!updateRequest) return;
    try {
      await updateUserApi(updateRequest);
      await fetchData(true);
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setIsFetching(false);
    }
  };

  const handleCreateUser = async (createData: IUpsertUserRequest) => {
    setIsFetching(true);
    store.dispatch(commonActions.setLoading());
    try {
      await createUserApi(createData);
      await fetchData(true);
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setIsFetching(false);
      store.dispatch(commonActions.removeLoading());
    }
  };

  const getSuggestCode = async (userType: string) => {
    try {
      const { list } = await fetchSuggestCodeApi({ user_type: userType });
      setSuggestCodeList(
        list.map((el: { user_type_code: string; name?: string; store_name?: string; company_name?: string; customer_name?: string }) => {
          // Get name from various possible fields
          const name = el.name || el.store_name || el.company_name || el.customer_name || "";
          // Format label as "name + user_type_code" or just "user_type_code" if no name
          const label = name ? `${el.user_type_code} (${name})` : el.user_type_code;
          return {
            label,
            value: el.user_type_code,
          };
        }),
      );
    } catch (e) {
      console.error("Error fetching suggest codes:", e);
      setSuggestCodeList([]);
    }
  };

  return {
    users,
    hasMore,
    query,
    tempQuery,
    setTempQuery,
    totalItems,
    setQuery,
    handleSubmitSearch: onSearch,
    disableSubmit: err,
    onNextPage,
    onChangeCategoryType,
    setErr,
    isFetching,
    handleUpdateUser,
    handleCreateUser,
    suggestCodeList,
    getSuggestCode,
    setSuggestCodeList,
  };
};

export default useUserList;
